import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Activity, Cpu, Eye, Search, Users, Zap, Calendar, Clock } from "lucide-react";
import { useApiStats } from "@/hooks/useApiStats";

const ApiStatsCard = () => {
  const { apiStats, isLoading, error, refetch } = useApiStats();

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Erro no Consumo de APIs</CardTitle>
          <CardDescription>Não foi possível carregar as estatísticas</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getApiIcon = (api: string) => {
    switch (api) {
      case 'openai': return <Cpu className="h-4 w-4" />;
      case 'deepseek': return <Zap className="h-4 w-4" />;
      case 'google': return <Search className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getApiColor = (api: string) => {
    switch (api) {
      case 'openai': return 'bg-green-500';
      case 'deepseek': return 'bg-blue-500';
      case 'google': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Consumo de APIs - {apiStats.periodInfo.currentPeriod}
        </CardTitle>
        <CardDescription>
          Monitoramento em tempo real do uso das APIs da plataforma
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-8 bg-muted rounded animate-pulse" />
            <div className="h-16 bg-muted rounded animate-pulse" />
          </div>
        ) : (
          <>
            {/* Informações do Período */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Período Atual
                </div>
                <span className="font-medium">
                  {apiStats.periodInfo.startDate} - {apiStats.periodInfo.endDate}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Progresso do mês</span>
                  <span className="font-medium">
                    {apiStats.periodInfo.daysInPeriod - apiStats.periodInfo.daysRemaining} de {apiStats.periodInfo.daysInPeriod} dias
                  </span>
                </div>
                <Progress value={apiStats.periodInfo.periodProgress} className="h-2" />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Próxima renovação
                </div>
                <span className="font-medium text-primary">
                  {apiStats.periodInfo.nextRenewal}
                </span>
              </div>
            </div>

            <div className="border-t pt-4">
            {/* Consumo Total de Créditos */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Créditos Utilizados</span>
                <span className="text-sm text-muted-foreground">
                  {formatNumber(apiStats.totalCredits)} / {formatNumber(apiStats.limitCredits)}
                </span>
              </div>
              <Progress 
                value={apiStats.usagePercentage} 
                className="h-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{apiStats.usagePercentage.toFixed(1)}% utilizado</span>
                <span>{apiStats.activeUsers} usuários ativos</span>
              </div>
            </div>

            {/* Status das APIs */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Status das APIs</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  {getApiIcon('openai')}
                  <span className="text-xs">OpenAI</span>
                  <Badge 
                    variant={apiStats.apiStatuses.openai ? "default" : "secondary"}
                    className={`text-xs ${apiStats.apiStatuses.openai ? 'bg-green-100 text-green-800' : ''}`}
                  >
                    {apiStats.apiStatuses.openai ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  {getApiIcon('deepseek')}
                  <span className="text-xs">DeepSeek</span>
                  <Badge 
                    variant={apiStats.apiStatuses.deepseek ? "default" : "secondary"}
                    className={`text-xs ${apiStats.apiStatuses.deepseek ? 'bg-blue-100 text-blue-800' : ''}`}
                  >
                    {apiStats.apiStatuses.deepseek ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span className="text-xs">G. Vision</span>
                  <Badge 
                    variant={apiStats.apiStatuses.googleVision ? "default" : "secondary"}
                    className={`text-xs ${apiStats.apiStatuses.googleVision ? 'bg-yellow-100 text-yellow-800' : ''}`}
                  >
                    {apiStats.apiStatuses.googleVision ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  <span className="text-xs">G. Search</span>
                  <Badge 
                    variant={apiStats.apiStatuses.googleSearch ? "default" : "secondary"}
                    className={`text-xs ${apiStats.apiStatuses.googleSearch ? 'bg-yellow-100 text-yellow-800' : ''}`}
                  >
                    {apiStats.apiStatuses.googleSearch ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Consumo por API */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Consumo por API</h4>
              <div className="space-y-2">
                {Object.entries(apiStats.consumptionByApi).map(([api, consumption]) => (
                  <div key={api} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getApiColor(api)}`} />
                      <span className="text-xs capitalize">
                        {api === 'google' ? 'Google APIs' : api}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatNumber(consumption.total)} créditos
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Usuários Ativos */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Usuários Online</span>
              </div>
              <span className="text-lg font-bold text-green-600">
                {apiStats.activeUsers}
              </span>
            </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ApiStatsCard;